import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SessionHelper } from '@src/app/helpers/sessionHelper';
import { environment } from '@src/environments/environment';
import { TaskEstimate } from '@src/app/models/taskestimate';
import { TimeEntry } from '@src/app/models/timeentry';
import { ProjectTimeSummary, ProjectTimeSummaryRow } from '@src/app/models/projecttimesummary';
import { TimeReportEntry } from '@src/app/models/timereportentry';
import { forkJoin, Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

export interface TimeReportFilters {
  from?: string;
  to?: string;
  customerId?: string;
  projectId?: string;
  personId?: string;
}

export interface TaskTimeSummary {
  estimateMinutes?: number;
  loggedMinutes: number;
}

// Calls the timereg-gated routes added in gateway/routes.js (backend).
@Injectable({
  providedIn: 'root'
})
export class TimeregistrationService {

  // One project-time-summary request per project, shared by every stage
  // header that asks for it (rather than one call per stage), cleared
  // whenever any write below succeeds so a re-render picks up fresh totals.
  private projectTimeSummaryCache = new Map<string, Observable<ProjectTimeSummary>>();

  constructor(private http: HttpClient) { }

  private httpOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      withCredentials: true
    };
  }

  // GET /organization/:organizationId/project/:projectId/task/:taskId/estimates
  // Oldest first: [0] is the baseline, the last entry is the current estimate.
  getTaskEstimates(projectId: string, taskId: string) {
    return this.http.get<TaskEstimate[]>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() +
      '/project/' + projectId + '/task/' + taskId + '/estimates',
      this.httpOptions()
    );
  }

  // POST /organization/:organizationId/project/:projectId/task/:taskId/estimates
  addTaskEstimate(projectId: string, taskId: string, minutes: number, comment?: string) {
    return this.http.post<TaskEstimate>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() +
      '/project/' + projectId + '/task/' + taskId + '/estimates',
      JSON.stringify({ minutes, comment: comment || undefined }),
      this.httpOptions()
    ).pipe(tap(() => this.projectTimeSummaryCache.clear()));
  }

  // GET /organization/:organizationId/project/:projectId/time-entries?taskId=
  getTaskTimeEntries(projectId: string, taskId: string) {
    return this.http.get<TimeEntry[]>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() +
      '/project/' + projectId + '/time-entries?taskId=' + encodeURIComponent(taskId),
      this.httpOptions()
    );
  }

  // POST /organization/:organizationId/project/:projectId/task/:taskId/time-entries
  logTimeEntry(projectId: string, taskId: string, workDate: string, minutes: number, note?: string) {
    return this.http.post<TimeEntry>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() +
      '/project/' + projectId + '/task/' + taskId + '/time-entries',
      JSON.stringify({ workDate, minutes, note: note || undefined }),
      this.httpOptions()
    ).pipe(tap(() => this.projectTimeSummaryCache.clear()));
  }

  // PATCH /organization/:organizationId/time-entries/:entryId
  updateTimeEntry(entryId: string, patch: { workDate?: string; minutes?: number; note?: string; billable?: boolean }) {
    return this.http.patch<TimeEntry>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() + '/time-entries/' + entryId,
      JSON.stringify(patch),
      this.httpOptions()
    ).pipe(tap(() => this.projectTimeSummaryCache.clear()));
  }

  // Current estimate (last of the history) + sum of logged time for one
  // task — for a compact "1/2.5h" style counter, not the full history/list.
  getTaskTimeSummary(projectId: string, taskId: string): Observable<TaskTimeSummary> {
    return forkJoin({
      estimates: this.getTaskEstimates(projectId, taskId),
      entries: this.getTaskTimeEntries(projectId, taskId),
    }).pipe(
      map(({ estimates, entries }) => ({
        estimateMinutes: estimates && estimates.length ? estimates[estimates.length - 1].minutes : undefined,
        loggedMinutes: (entries || []).reduce((sum, entry) => sum + (entry.minutes || 0), 0),
      }))
    );
  }

  // Adhoc task equivalents of the four calls above (no project in the URL).
  // Kept as separate methods rather than an optional projectId param on the
  // existing ones, so the already-working project-scoped call sites don't
  // need to change.

  // GET /organization/:organizationId/tasks/adhoc/:taskId/estimates
  getAdhocTaskEstimates(taskId: string) {
    return this.http.get<TaskEstimate[]>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() +
      '/tasks/adhoc/' + taskId + '/estimates',
      this.httpOptions()
    );
  }

  // POST /organization/:organizationId/tasks/adhoc/:taskId/estimates
  addAdhocTaskEstimate(taskId: string, minutes: number, comment?: string) {
    return this.http.post<TaskEstimate>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() +
      '/tasks/adhoc/' + taskId + '/estimates',
      JSON.stringify({ minutes, comment: comment || undefined }),
      this.httpOptions()
    );
  }

  // GET /organization/:organizationId/tasks/adhoc/:taskId/time-entries
  getAdhocTaskTimeEntries(taskId: string) {
    return this.http.get<TimeEntry[]>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() +
      '/tasks/adhoc/' + taskId + '/time-entries',
      this.httpOptions()
    );
  }

  // POST /organization/:organizationId/tasks/adhoc/:taskId/time-entries
  logAdhocTimeEntry(taskId: string, workDate: string, minutes: number, note?: string) {
    return this.http.post<TimeEntry>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() +
      '/tasks/adhoc/' + taskId + '/time-entries',
      JSON.stringify({ workDate, minutes, note: note || undefined }),
      this.httpOptions()
    );
  }

  // DELETE /organization/:organizationId/time-entries/:entryId
  deleteTimeEntry(entryId: string) {
    return this.http.delete<any>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() + '/time-entries/' + entryId,
      this.httpOptions()
    ).pipe(tap(() => this.projectTimeSummaryCache.clear()));
  }

  // GET /organization/:organizationId/project/:projectId/time-summary
  // Per-stage totals + the project total. Uncached — callers that render
  // many stages at once should go through getProjectTimeSummaryCached
  // instead, so they share one request rather than firing one each.
  getProjectTimeSummary(projectId: string): Observable<ProjectTimeSummary> {
    return this.http.get<ProjectTimeSummary>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() +
      '/project/' + projectId + '/time-summary',
      this.httpOptions()
    );
  }

  getProjectTimeSummaryCached(projectId: string): Observable<ProjectTimeSummary> {
    if (!this.projectTimeSummaryCache.has(projectId)) {
      this.projectTimeSummaryCache.set(projectId, this.getProjectTimeSummary(projectId).pipe(shareReplay(1)));
    }
    return this.projectTimeSummaryCache.get(projectId);
  }

  // GET /organization/:organizationId/projects/time-summary
  // One row per project the caller can view — for a project list, not a
  // single project.
  getOrgTimeSummary(): Observable<ProjectTimeSummaryRow[]> {
    return this.http.get<ProjectTimeSummaryRow[]>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() + '/projects/time-summary',
      this.httpOptions()
    );
  }

  // GET /organization/:organizationId/time-entries/report
  // Enriched with project/customer/task names, backs the time overview
  // page's My time and Team tabs. The backend scopes this to "my own
  // entries" for anyone below org-edit, regardless of the filters sent.
  getOrgTimeReport(filters: TimeReportFilters = {}): Observable<TimeReportEntry[]> {
    const params: string[] = [];
    if (filters.from) { params.push('from=' + encodeURIComponent(filters.from)); }
    if (filters.to) { params.push('to=' + encodeURIComponent(filters.to)); }
    if (filters.customerId) { params.push('customerId=' + encodeURIComponent(filters.customerId)); }
    if (filters.projectId) { params.push('projectId=' + encodeURIComponent(filters.projectId)); }
    if (filters.personId) { params.push('personId=' + encodeURIComponent(filters.personId)); }
    const query = params.length ? '?' + params.join('&') : '';

    return this.http.get<TimeReportEntry[]>(
      environment.apiUrl + '/organization/' + SessionHelper.getChoosenOrg() + '/time-entries/report' + query,
      this.httpOptions()
    );
  }
}
